import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'motion/react';
import { Mail } from 'lucide-react';
import { Button } from '@/components/ui';
import { GradientDots } from '@/components/ui/GradientDots';
import { OperatorWordmark } from '@/components/auth/OperatorWordmark';
import { GlowInput } from '@/components/auth/GlowInput';

export type ForgotPasswordState = 'form' | 'success';

export interface ForgotPasswordViewProps {
  state: ForgotPasswordState;
  email: string;
  error: string;
  isLoading: boolean;
  onEmailChange: (value: string) => void;
  onSubmit: (e: React.FormEvent) => void;
}

const fadeMotion = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  transition: { duration: 0.25, ease: 'easeOut' as const },
};

export function ForgotPasswordView({
  state,
  email,
  error,
  isLoading,
  onEmailChange,
  onSubmit,
}: ForgotPasswordViewProps) {
  return (
    <div
      className="relative min-h-screen flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8 py-12"
      style={{ backgroundColor: 'hsl(var(--auth-bg))' }}
    >
      <GradientDots />

      <div className="relative z-10 w-full max-w-3xl rounded-[32px] border border-foreground/15 bg-card/95 dark:bg-card/15 p-6 shadow-[0_80px_160px_-30px_rgba(0,0,0,0.8),0_40px_80px_-20px_rgba(0,0,0,0.6)] backdrop-blur-3xl sm:p-12 md:p-16 lg:p-20 2xl:p-28 2xl:max-w-4xl">
        <div className="flex flex-col items-center text-center">
          <OperatorWordmark size="xl" />
          <p className="mt-6 text-[10px] tracking-[0.15em] sm:text-xs sm:tracking-[0.25em] uppercase text-muted-foreground">
            Reset Your Password
          </p>
        </div>

        <div className="mt-10 min-h-[248px]">
          {state === 'success' ? (
            <motion.div
              {...fadeMotion}
              className="flex min-h-[248px] flex-col items-center justify-center gap-3 text-center text-sm text-muted-foreground"
            >
              <p>
                We've sent a password reset link to{' '}
                <strong className="text-foreground">{email}</strong>.
              </p>
              <p>Check your inbox and follow the instructions.</p>
            </motion.div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-5" noValidate>
              {/* Header slot — kept for layout symmetry with SignInView. */}
              <div className="min-h-[24px]" />

              <GlowInput
                type="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => onEmailChange(e.target.value)}
                placeholder="Enter email address"
                aria-label="Email address"
                icon={<Mail />}
              />

              {/* Footer slot — error message. Padded px-5 to align with the
                  input's text indent. */}
              <div className="min-h-[24px] flex items-center px-5 text-sm">
                <AnimatePresence initial={false}>
                  {error && (
                    <motion.span
                      key="error"
                      {...fadeMotion}
                      role="alert"
                      className="text-[hsl(335_85%_70%)]"
                    >
                      {error}
                    </motion.span>
                  )}
                </AnimatePresence>
              </div>

              <Button
                type="submit"
                className="auth-button-hue w-full text-white border shadow-lg bg-[hsl(252_8%_20%)] border-[hsl(252_10%_32%)] hover:bg-[hsl(252_10%_26%)] hover:border-[hsl(252_12%_40%)]"
                size="lg"
                isLoading={isLoading}
              >
                Send reset link
              </Button>

              <div className="min-h-[20px] text-center text-sm">
                <Link
                  to="/signin"
                  className="font-medium text-primary-600 hover:text-primary-500"
                >
                  Back to sign in
                </Link>
              </div>
            </form>
          )}
        </div>

        <p className="mt-10 text-center text-xs text-muted-foreground">
          By continuing, you agree to our{' '}
          <a
            href="/terms"
            className="underline underline-offset-2 hover:text-foreground"
          >
            Terms of Service
          </a>{' '}
          and{' '}
          <a
            href="/privacy"
            className="underline underline-offset-2 hover:text-foreground"
          >
            Privacy Policy
          </a>
          .
        </p>
      </div>
    </div>
  );
}
